/**
 * iOS-safe body scroll lock for modals and sheets.
 *
 * `body { overflow: hidden }` alone does nothing on iOS Safari: the page behind an open
 * modal keeps scrolling with the finger (director iPhone 8 Plus recording 2026-09-28,
 * "modal active means background page is held in position"). Pinning the body with
 * `position: fixed` at the current scroll offset is the one pattern iOS honours; the
 * offset is restored on unlock so the page does not jump.
 *
 * Deliberately NO `overflow: hidden`: `html { overflow-x: clip }` stops body overflow from
 * propagating to the viewport, so `overflow: hidden` would turn <body> into the scroll
 * container and every sticky bar would jump while locked (Opus review 2026-09-28).
 *
 * Reference-counted so stacked overlays (3D peek over a preview, sheet over a banner)
 * each release only their own hold; the body is restored when the last one closes.
 * The saved offset is restored only on the SAME route: a modal that unmounts because
 * its link navigated away must not scroll the new page to the old position.
 *
 * `isBodyScrollLocked()` lets scroll listeners ignore the synthetic `scroll` event the
 * lock fires (window.scrollY reads 0 while the body is fixed).
 */
let holds = 0;
let saved: { scrollY: number; href: string; position: string; top: string; left: string; right: string; width: string } | null = null;

export function isBodyScrollLocked(): boolean {
  return holds > 0;
}

export function lockBodyScroll(): () => void {
  if (typeof document === 'undefined') return () => {};
  const body = document.body;
  if (holds === 0) {
    const scrollY = window.scrollY || window.pageYOffset || 0;
    saved = {
      scrollY,
      href: window.location.pathname + window.location.search,
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
    };
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    // A fixed <body> becomes its own stacking context, so root-level chrome (the standalone
    // status-bar strip, html::before) would paint over the modals portaled into it; CSS hides
    // that strip while this flag is set.
    document.documentElement.setAttribute('data-scroll-locked', '');
  }
  holds += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holds = Math.max(0, holds - 1);
    if (holds === 0 && saved) {
      const s = saved;
      saved = null;
      body.style.position = s.position;
      body.style.top = s.top;
      body.style.left = s.left;
      body.style.right = s.right;
      body.style.width = s.width;
      document.documentElement.removeAttribute('data-scroll-locked');
      if (window.location.pathname + window.location.search === s.href) window.scrollTo(0, s.scrollY);
    }
  };
}
